import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useTheme } from '../contexts/ThemeContext';
import API from '../services/api';
import MFASetup from '../components/MFASetup';
import Modal from '../components/Modal';
import UserAvatar from '../components/UserAvatar';
import PasswordChecklist from '../components/PasswordChecklist';
import { passwordProblems } from '../utils/passwordPolicy';
import { formatIsoDate } from '../utils/formatDateValue';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';

interface MFAStatus {
  mfaEnabled: boolean;
  mfaVerifiedAt: string | null;
  backupCodesCount: number;
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  DOCUMENTER: 'You enter cases and see only your own submissions.',
  RESEARCHER: 'You enter and edit cases, research them, and see cases in your country scope.',
  APPROVER: 'You review, approve and reject cases, decide duplicates, and see every case.',
  ADMIN: 'You can do everything, including managing users and the scraper.',
};

// Each card owns its own feedback line, shown next to the action that caused it.
const Notice: React.FC<{ kind: 'ok' | 'err'; children: React.ReactNode }> = ({ kind, children }) => (
  <div
    role={kind === 'err' ? 'alert' : 'status'}
    className={`mb-4 rounded-md border px-3 py-2 text-sm ${kind === 'err'
      ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300'
      : 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'}`}
  >
    {children}
  </div>
);

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { theme, toggleTheme } = useTheme();

  // ---- your details ----
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [organization, setOrganization] = useState(user?.organization || '');
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsMsg, setDetailsMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    setFirstName(user?.firstName || ''); setLastName(user?.lastName || ''); setOrganization(user?.organization || '');
  }, [user?.firstName, user?.lastName, user?.organization]);
  const detailsDirty = firstName !== (user?.firstName || '') || lastName !== (user?.lastName || '') || organization !== (user?.organization || '');
  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setDetailsSaving(true); setDetailsMsg(null);
    try {
      const r = await API.put(`/api/users/${user.id}`, { firstName: firstName.trim(), lastName: lastName.trim(), organization: organization.trim() || undefined });
      const u = r.data.user || {};
      updateUser({ firstName: u.firstName ?? firstName.trim(), lastName: u.lastName ?? lastName.trim(), organization: u.organization ?? organization.trim() });
      setDetailsMsg({ kind: 'ok', text: 'Details saved.' });
    } catch (err: any) {
      const list: Array<{ msg: string }> = err.response?.data?.errors || [];
      setDetailsMsg({ kind: 'err', text: list.length ? list.map(x => x.msg).join(' ') : (err.response?.data?.message || 'Could not save your details') });
    } finally {
      setDetailsSaving(false);
    }
  };

  // ---- password ----
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (!user?.id) return;
    const problems = passwordProblems(newPassword);
    if (problems.length) { setPwMsg({ kind: 'err', text: `Password needs: ${problems.map(r => r.label.toLowerCase()).join(', ')}.` }); return; }
    if (newPassword !== confirmNewPassword) { setPwMsg({ kind: 'err', text: 'New passwords do not match.' }); return; }
    setPwLoading(true);
    try {
      await API.put(`/api/users/${user.id}/password`, { currentPassword, newPassword });
      setPwMsg({ kind: 'ok', text: 'Password changed.' });
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (err: any) {
      setPwMsg({ kind: 'err', text: err.response?.data?.message || 'Could not change the password' });
    } finally {
      setPwLoading(false);
    }
  };

  // ---- two-factor ----
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [mfaLoadFailed, setMfaLoadFailed] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaMsg, setMfaMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [mfaBusy, setMfaBusy] = useState(false);
  // One password dialog serves both "disable" and "regenerate backup codes".
  const [passwordFor, setPasswordFor] = useState<'disable' | 'regenerate' | null>(null);
  const [mfaPassword, setMfaPassword] = useState('');
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const fetchMFAStatus = async () => {
    try {
      const r = await API.get('/api/mfa/status');
      if (r.data.success) { setMfaStatus(r.data.data); setMfaLoadFailed(false); }
    } catch {
      setMfaLoadFailed(true);
    }
  };
  useEffect(() => { void fetchMFAStatus(); }, []);

  const submitMfaPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordFor) return;
    setMfaBusy(true); setMfaMsg(null);
    try {
      if (passwordFor === 'disable') {
        const r = await API.post('/api/mfa/disable', { password: mfaPassword });
        if (r.data.success) { setMfaMsg({ kind: 'ok', text: 'Two-factor authentication is off.' }); await fetchMFAStatus(); }
      } else {
        const r = await API.post('/api/mfa/regenerate-backup-codes', { password: mfaPassword });
        if (r.data.success) { setNewCodes(r.data.data.backupCodes); await fetchMFAStatus(); }
      }
      setPasswordFor(null); setMfaPassword('');
    } catch (err: any) {
      setMfaMsg({ kind: 'err', text: err.response?.data?.message || 'That did not work' });
      setPasswordFor(null); setMfaPassword('');
    } finally {
      setMfaBusy(false);
    }
  };
  const startRegenerate = async () => {
    const ok = await confirm({
      title: 'Regenerate backup codes?',
      message: 'Your current backup codes stop working the moment new ones are made. You will be asked for your password.',
      confirmLabel: 'Regenerate',
      cancelLabel: 'Keep current codes',
      variant: 'danger',
    });
    if (ok) setPasswordFor('regenerate');
  };
  const copyCodes = async () => {
    if (!newCodes) return;
    try { await navigator.clipboard.writeText(newCodes.join('\n')); showToast('success', 'Backup codes copied'); }
    catch { showToast('error', 'Could not copy; use Download instead'); }
  };
  const downloadCodes = () => {
    if (!newCodes) return;
    const blob = new Blob(['IPRI MFA backup codes\n\n' + newCodes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'ipri-mfa-backup-codes.txt'; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const input = 'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm';
  const label = 'block text-sm font-medium text-gray-700 dark:text-gray-300';
  const primary = 'px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed';
  const secondary = 'px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';
  const card = 'bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6 mb-6';

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="flex items-center gap-4 mb-8">
        <UserAvatar user={user} size="lg" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-heading">{user?.firstName} {user?.lastName}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{user?.email} · {user?.role ? user.role.charAt(0) + user.role.slice(1).toLowerCase() : ''}</p>
        </div>
      </div>

      {/* Your details */}
      <div className={card}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Your details</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Your name and organisation appear on the cases you document. Email and role are set by an admin.</p>
        {detailsMsg && <Notice kind={detailsMsg.kind}>{detailsMsg.text}</Notice>}
        <form onSubmit={saveDetails} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="profile-first-name" className={label}>First name</label>
            <input id="profile-first-name" type="text" required minLength={2} maxLength={50} value={firstName} onChange={(e) => setFirstName(e.target.value)} className={input} />
          </div>
          <div>
            <label htmlFor="profile-last-name" className={label}>Last name</label>
            <input id="profile-last-name" type="text" required minLength={2} maxLength={50} value={lastName} onChange={(e) => setLastName(e.target.value)} className={input} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="profile-organization" className={label}>Organisation</label>
            <input id="profile-organization" type="text" maxLength={100} value={organization} onChange={(e) => setOrganization(e.target.value)} className={input} placeholder="Organisation or affiliation" />
          </div>
          <div>
            <span className={label}>Email</span>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user?.email}</p>
          </div>
          <div>
            <span className={label}>Role</span>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user?.role ? user.role.charAt(0) + user.role.slice(1).toLowerCase() : '—'}</p>
            {user?.role && <p className="text-xs text-gray-500 dark:text-gray-400">{ROLE_DESCRIPTIONS[user.role]}</p>}
          </div>
          <div className="sm:col-span-2">
            <span className={label}>Country scope</span>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {user?.role === 'ADMIN' || user?.role === 'APPROVER'
                ? 'Every case, no country scope.'
                : user?.countries && user.countries.length > 0
                  ? user.countries.join(', ')
                  : user?.role === 'DOCUMENTER' ? 'Your own submissions, every country.' : 'Every country.'}
            </p>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <button type="submit" disabled={!detailsDirty || detailsSaving} className={primary}>{detailsSaving ? 'Saving…' : 'Save details'}</button>
          </div>
        </form>
      </div>

      {/* Appearance */}
      <div className={card}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Appearance</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Remembered on this browser.</p>
        <button type="button" onClick={toggleTheme} className={`${secondary} inline-flex items-center gap-2`} aria-pressed={theme === 'dark'}>
          {theme === 'dark' ? <SunIcon className="h-4 w-4" aria-hidden="true" /> : <MoonIcon className="h-4 w-4" aria-hidden="true" />}
          {theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        </button>
      </div>

      {/* Password */}
      <div className={card}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Change password</h2>
        {pwMsg && <Notice kind={pwMsg.kind}>{pwMsg.text}</Notice>}
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label htmlFor="current-password" className={label}>Current password</label>
            <input id="current-password" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={input} required />
          </div>
          <div>
            <label htmlFor="new-password" className={label}>New password</label>
            <input id="new-password" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={input} required />
            <PasswordChecklist password={newPassword} />
          </div>
          <div>
            <label htmlFor="confirm-new-password" className={label}>Confirm new password</label>
            <input id="confirm-new-password" type="password" autoComplete="new-password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} className={input} required />
          </div>
          <button type="submit" disabled={pwLoading} className={primary}>{pwLoading ? 'Changing…' : 'Change password'}</button>
        </form>
      </div>

      {/* Two-factor authentication */}
      <div className={card}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Two-factor authentication</h2>
        {mfaMsg && <Notice kind={mfaMsg.kind}>{mfaMsg.text}</Notice>}
        {mfaLoadFailed ? (
          <Notice kind="err">Couldn't load your two-factor status. <button type="button" onClick={fetchMFAStatus} className="underline">Try again</button></Notice>
        ) : !mfaStatus ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" role="status">Loading…</p>
        ) : mfaStatus.mfaEnabled ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold">On.</span> A code from your authenticator app is required when you sign in.
              {mfaStatus.mfaVerifiedAt && <span className="text-gray-500 dark:text-gray-400"> Last used {formatIsoDate(mfaStatus.mfaVerifiedAt)}.</span>}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Backup codes left: <span className="font-semibold">{mfaStatus.backupCodesCount}</span>.
              <span className="text-gray-500 dark:text-gray-400"> A backup code signs you in if you lose your authenticator; each works once.</span>
            </p>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={startRegenerate} disabled={mfaBusy} className={secondary}>Regenerate backup codes</button>
              <button type="button" onClick={() => setPasswordFor('disable')} disabled={mfaBusy} className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50">Turn off two-factor</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300"><span className="font-semibold">Off.</span> Adding a second factor protects the case record if your password leaks.</p>
            <button type="button" onClick={() => setShowMFASetup(true)} className={primary}>Set up two-factor authentication</button>
          </div>
        )}
      </div>

      {showMFASetup && (
        <MFASetup
          onClose={() => setShowMFASetup(false)}
          onSuccess={() => { setShowMFASetup(false); void fetchMFAStatus(); setMfaMsg({ kind: 'ok', text: 'Two-factor authentication is on.' }); }}
        />
      )}

      {passwordFor && (
        <Modal
          title={passwordFor === 'disable' ? 'Turn off two-factor authentication' : 'Regenerate backup codes'}
          description="Confirm with your password."
          onClose={() => { setPasswordFor(null); setMfaPassword(''); }}
          size="sm"
        >
          <form onSubmit={submitMfaPassword} className="space-y-4">
            <div>
              <label htmlFor="mfa-password" className={label}>Password</label>
              <input id="mfa-password" type="password" autoComplete="current-password" value={mfaPassword} onChange={(e) => setMfaPassword(e.target.value)} className={input} required />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setPasswordFor(null); setMfaPassword(''); }} className={secondary}>Cancel</button>
              <button type="submit" disabled={mfaBusy} className={passwordFor === 'disable' ? 'px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50' : primary}>
                {mfaBusy ? 'Working…' : passwordFor === 'disable' ? 'Turn off' : 'Regenerate'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {newCodes && (
        <Modal title="Your new backup codes" description="Each code works once. Keep them somewhere safe; they are not shown again." onClose={() => setNewCodes(null)} size="sm">
          <ul className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-gray-900 dark:text-gray-100 mb-4">
            {newCodes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={copyCodes} className={secondary}>Copy</button>
            <button type="button" onClick={downloadCodes} className={secondary}>Download</button>
            <button type="button" onClick={() => setNewCodes(null)} className={primary}>Done</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Profile;
